import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import {
  type FeedAction,
  type FeedActionContext,
  type FeedDataSourceLoader,
  type FeedItemDefinition,
  type FeedDataState,
  type TableFeedItem,
  type TileGridFeedItem,
  type TextFeedItem,
  type ChatMessageFeedItem,
  type RecommendedActionsFeedItem,
  type ObjectListFeedItem,
  type ObjectDetailFeedItem,
  type CustomFeedItem
} from "./feedTypes";
import { TextCard } from "./components/TextCard.js";
import { TileGridCard } from "./components/TileGridCard.js";
import { TableCard } from "./components/TableCard.js";
import { Card } from "./Card.js";
import { Card as ChatMessageCard } from "./ChatMessageCard.js";
import { RecommendedActionsCard } from "./RecommendedActionsCard.js";
import { ObjectListCard } from "./ObjectListCard.js";
import { ObjectDetailCard } from "./ObjectDetailCard.js";
import { FeedCardShell } from "./components/FeedCardShell.js";
import "./FeedRenderer.css";

type FeedComponentRenderer = (props: FeedCardComponentProps) => ReactElement | null;

const COMPONENT_REGISTRY: Record<string, FeedComponentRenderer> = {
  text: ({ item, ...rest }) => (
    <FeedCardShell item={item} {...rest}>
      <TextCard item={item as TextFeedItem} {...rest} />
    </FeedCardShell>
  ),
  tiles: ({ item, ...rest }) => (
    <FeedCardShell item={item} {...rest}>
      <TileGridCard item={item as TileGridFeedItem} {...rest} />
    </FeedCardShell>
  ),
  table: ({ item, ...rest }) => (
    <FeedCardShell item={item} {...rest}>
      <TableCard item={item as TableFeedItem} {...rest} />
    </FeedCardShell>
  ),
  "chat-message": ({ item, ...rest }) => {
    try {
      const chatItem = item as ChatMessageFeedItem;

      // Check if camera upload is required
      const requiresCameraUpload = (chatItem.config?.messagePayload as any)?.requiresCameraUpload === true;

      // Check if workflow UI is present
      const hasWorkflowUI = !!chatItem.config?.workflowUI;

      // Check if recommended actions are present
      const hasRecommendedActions = Array.isArray((chatItem.config?.messagePayload as any)?.recommendedActions)
        && ((chatItem.config?.messagePayload as any)?.recommendedActions as any[]).length > 0;

      // Check if download URL is present (e.g., for certificate downloads)
      const hasDownloadUrl = !!(chatItem.config?.messagePayload as any)?.metadata?.downloadUrl;

      if (requiresCameraUpload || hasWorkflowUI || hasRecommendedActions || hasDownloadUrl) {
        // Use ChatMessageCard for camera upload, workflow UI, recommended actions, or download functionality
        return <ChatMessageCard item={chatItem} {...rest} onReload={rest.requestReload} />;
      }

      // Default: Render raw HTML content directly without card wrapper
      const answer = chatItem.config?.answer || '';
      const innerHTML = useMemo(() => ({ __html: answer }), [answer]);

      return (
        <div
          key={`chat-${item.id || 'msg'}`}
          style={{
            width: '100%',
            maxWidth: '100%',
            padding: '1rem 0',
            lineHeight: '1.6'
          }}
          dangerouslySetInnerHTML={innerHTML}
        />
      );
    } catch (error) {
      console.error('Error rendering chat message:', error, item);
      return (
        <div style={{ padding: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '8px' }}>
          <strong>Fehler beim Rendern der Nachricht</strong>
          <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{String(error)}</pre>
        </div>
      );
    }
  },
  "recommended-actions": ({ item, ...rest }) => {
    try {
      return <RecommendedActionsCard item={item as RecommendedActionsFeedItem} {...rest} onReload={rest.requestReload} />;
    } catch (error) {
      console.error('Error rendering RecommendedActionsCard:', error, item);
      return (
        <div style={{ padding: '1rem', background: '#fee', border: '1px solid #fcc', borderRadius: '8px' }}>
          <strong>Fehler beim Rendern der Aktionskarte</strong>
          <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{String(error)}</pre>
        </div>
      );
    }
  },
  "object-list": ({ item, ...rest }) => (
    <ObjectListCard item={item as ObjectListFeedItem} {...rest} onReload={rest.requestReload} />
  ),
  "object-detail": ({ item, ...rest }) => (
    <ObjectDetailCard item={item as any} {...rest} onReload={rest.requestReload} />
  ),
  "custom": ({ item, ...rest }) => (
    <FeedCardShell item={item} {...rest}>
      {(item as CustomFeedItem).config.render()}
    </FeedCardShell>
  )
};

export interface FeedCardComponentProps {
  item: FeedItemDefinition;
  data: unknown;
  loading: boolean;
  error?: string | null;
  onAction: FeedActionHandler;
  requestReload: () => void;
  onTitleCommandExecute?: (itemId: string, command: string) => void;
  onTitleFilter?: (itemId: string, filterText: string) => void;
  isFullscreen?: boolean;
  chatHistory?: string[];
}

export type FeedActionHandler = (action: FeedAction, context?: FeedActionContext) => void;

export interface FeedRendererProps {
  items: FeedItemDefinition[];
  dataSources?: Record<string, FeedDataSourceLoader>;
  className?: string;
  onAction?: FeedActionHandler;
  fullscreenCardId?: string | null;
  chatHistory?: string[];
  onTitleCommandExecute?: (itemId: string, command: string) => void;
  onTitleFilter?: (itemId: string, filterText: string) => void;
}

interface DataSourceState {
  [key: string]: FeedDataState;
}

interface ReloadCounters {
  [key: string]: number;
}

function useFeedData(
  items: FeedItemDefinition[],
  loaders: Record<string, FeedDataSourceLoader>
): [DataSourceState, (key: string) => void] {
  const [state, setState] = useState<DataSourceState>({});
  const [reloadCounters, setReloadCounters] = useState<ReloadCounters>({});
  const pendingRequests = useRef<Map<string, AbortController>>(new Map());

  const uniqueKeys = useMemo(() => {
    return Array.from(
      new Set(
        items
          .map((item) => item.dataSource)
          .filter((value): value is string => Boolean(value))
      )
    );
  }, [items]);

  const reloadDataSource = useCallback((key: string) => {
    if (!key) return;
    setReloadCounters((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  }, []);

  useEffect(() => {
    const controllers = pendingRequests.current;

    const loadData = async (key: string) => {
      const loader = loaders[key];
      if (!loader) {
        setState((prev) => ({
          ...prev,
          [key]: { loading: false, error: `Kein DataSource Loader für "${key}" registriert.` }
        }));
        return;
      }

      const existingController = controllers.get(key);
      existingController?.abort();

      const controller = new AbortController();
      controllers.set(key, controller);

      setState((prev) => ({
        ...prev,
        [key]: {
          data: prev[key]?.data,
          loading: true,
          error: undefined
        }
      }));

      try {
        const data = await loader({ signal: controller.signal });
        setState((prev) => ({
          ...prev,
          [key]: {
            data,
            loading: false,
            error: undefined
          }
        }));
      } catch (error) {
        if ((error as Error)?.name === "AbortError") {
          return;
        }
        setState((prev) => ({
          ...prev,
          [key]: {
            data: prev[key]?.data,
            loading: false,
            error: error instanceof Error ? error.message : "Unbekannter Fehler"
          }
        }));
      }
    };

    uniqueKeys.forEach(loadData);

    return () => {
      uniqueKeys.forEach((key) => {
        const controller = controllers.get(key);
        controller?.abort();
      });
    };
  }, [loaders, uniqueKeys, reloadCounters]);

  return [state, reloadDataSource];
}

function useFeedActionHandler(reload: (key: string) => void): FeedActionHandler {
  const navigate = useNavigate();

  return useCallback(
    (action, context) => {
      switch (action.type) {
        case "reloadData": {
          if (action.target) {
            reload(action.target);
          }
          break;
        }
        case "navigate":
        case "openForm": {
          if (!action.target) return;
          const path = resolvePath(action, context);
          if (path) navigate(path);
          break;
        }
        case "openDetail": {
          if (!action.target) return;
          const path = resolvePath(action, context);
          if (path) navigate(path);
          break;
        }
        case "openModal": {
          // Future extension: hook into modal service
          console.info("openModal action invoked", action, context);
          break;
        }
        case "command": {
          console.info("command action invoked", action, context);
          break;
        }
        default:
          break;
      }
    },
    [navigate, reload]
  );
}

function resolvePath(action: FeedAction, context?: FeedActionContext): string | null {
  if (!action.target) return null;

  const paramValue = action.paramKey ? lookupContextValue(action.paramKey, context) : undefined;

  let path = action.target;
  if (action.paramKey && paramValue !== undefined) {
    path = path.replace(`:${action.paramKey}`, encodeURIComponent(String(paramValue)));
  }

  if (action.query && Object.keys(action.query).length > 0) {
    const params = new URLSearchParams(action.query);
    if (action.paramKey && paramValue !== undefined && !params.has(action.paramKey)) {
      params.set(action.paramKey, String(paramValue));
    }
    const queryString = params.toString();
    path = queryString ? `${path}?${queryString}` : path;
  }

  return path;
}

function lookupContextValue(key: string, context?: FeedActionContext): unknown {
  if (!context) return undefined;
  if (context.row && key in context.row) {
    return context.row[key];
  }
  if (context.data && typeof context.data === "object" && context.data !== null && key in context.data) {
    return (context.data as Record<string, unknown>)[key];
  }
  return undefined;
}

export function FeedRenderer({
  items,
  dataSources = {},
  className,
  onAction: externalOnAction,
  fullscreenCardId,
  chatHistory = [],
  onTitleCommandExecute,
  onTitleFilter
}: FeedRendererProps) {
  const [dataState, reload] = useFeedData(items, dataSources);
  const internalHandleAction = useFeedActionHandler(reload);

  const handleAction: FeedActionHandler = useCallback((action, context) => {
    internalHandleAction(action, context);
    if (externalOnAction) {
      externalOnAction(action, context);
    }
  }, [internalHandleAction, externalOnAction]);

  return (
    <div className={className}>
      {items.map((item) => {
        const Component = COMPONENT_REGISTRY[item.type];
        const dataSourceKey = item.dataSource;
        const currentState: FeedDataState | undefined = dataSourceKey ? dataState[dataSourceKey] : undefined;

        if (!Component) {
          return (
            <div key={item.id} className="feed-card feed-card--missing">
              <header className="feed-card-header">
                <div>
                  <h3 className="feed-card-title">{item.title}</h3>
                  {item.description && <p className="feed-card-description">{item.description}</p>}
                </div>
              </header>
              <div className="feed-card-body">
                <p>Kein Renderer für den Typ "{item.type}" implementiert.</p>
              </div>
            </div>
          );
        }

        // Create a stable key that includes config changes for chat messages
        const itemKey = item.type === 'chat-message'
          ? `${item.id}-${(item as ChatMessageFeedItem).config?.question || ''}-${(item as ChatMessageFeedItem).config?.answer || ''}`.substring(0, 150)
          : item.id;

        return (
          <Component
            key={itemKey}
            item={item}
            data={currentState?.data}
            error={currentState?.error}
            loading={currentState?.loading ?? false}
            onAction={handleAction}
            requestReload={() => {
              if (dataSourceKey) reload(dataSourceKey);
            }}
            onTitleCommandExecute={onTitleCommandExecute}
            onTitleFilter={onTitleFilter}
            isFullscreen={fullscreenCardId === item.id}
            chatHistory={chatHistory}
          />
        );
      })}
    </div>
  );
}
